/**
 * Fenêtre « Direction de la station » (touches F, G, J) :
 * - Fréquentation : carte de chaleur installation x quart d'heure pour
 *   chaque journée gardée, bouchons détectés, détail d'une installation, et
 *   vue « Saison » (une case par journée) ;
 * - Finances, Stocks, Tarifs : gestion du gérant ;
 * - Journal : faits marquants datés (pannes, livraisons, bouchons, bilans).
 */
import type { JSX } from 'preact';
import { useEffect, useState } from 'preact/hooks';
import { CalendarDays, History, ScrollText, TrendingDown, TrendingUp, X } from 'lucide-preact';
import { PgCrate, PgDrop, PgFuel, PgPeople, PgTicket, type AnyIcon } from './icons';
import {
  ECONOMY,
  FACILITY_GROUP,
  MAINTENANCE_LEVELS,
  SUPPLY_LABEL,
  bucketRange,
  calendarDay,
  dayCosts,
  dayIndex,
  dayRevenue,
  fairTicketPrice,
  formatHour,
  minuteOfDay,
  seriesValue,
  summarizeDay,
  summarizeFacility,
  type FacilityMeta,
  type FacilitySummary,
  type SupplyKind,
  type UsageDay,
} from '@snowchain/shared';
import { Button, EventNotice, Meter, StatTile, type Tone } from './components';
import { command, focusEntity, hud } from './context';
import { euros, n0, n1, n2 } from './format';
import { FACILITY_ICON, levelColor, levelLabel } from './labels';
import { HourAxis, Sparkline, UsageBars, UsageStrip } from './charts';
import { Fact, LevelChip, LevelLegend, Tabs } from './game-ui';
import { currentBucket, dayJams, dayLabel, describeBucket, describeJam, jamShort, seriesMax, seriesSum, todayIndex, usageDay, waitText } from './report';
import { ui, type OfficeTab } from './ui-state';

const TABS: { id: OfficeTab; label: string }[] = [
  { id: 'frequentation', label: 'Fréquentation' },
  { id: 'finances', label: 'Finances' },
  { id: 'stocks', label: 'Stocks' },
  { id: 'tarifs', label: 'Tarifs' },
  { id: 'journal', label: 'Journal' },
];

export function Office() {
  const tab = ui.office.value;
  if (!tab) return null;
  void ui.tick.value;
  const store = hud.store;
  const r = store?.replica;
  if (!store || !r) return null;
  return (
    <section class="sc-hud g-panel g-office" role="dialog" aria-label="Direction de la station">
      <div class="g-office-head">
        <span class="g-office-title">Direction · {r.station.name}</span>
        <Tabs value={tab} options={TABS} onChange={(v) => (ui.office.value = v)} />
        <button class="sc-ibtn sc-ibtn-sm sc-ibtn-ghost" aria-label="Fermer (Échap)" onClick={() => (ui.office.value = null)}>
          <X size={15} />
        </button>
      </div>
      <div class="g-office-body">
        {tab === 'frequentation' && <Frequentation />}
        {tab === 'finances' && <Finances />}
        {tab === 'stocks' && <Stocks />}
        {tab === 'tarifs' && <Tarifs />}
        {tab === 'journal' && <Journal />}
      </div>
    </section>
  );
}

/* ------------------------------------------------------------------ */
/* Fréquentation                                                        */
/* ------------------------------------------------------------------ */

function groupOrder(m: { kind: FacilityMeta['kind'] }): number {
  return FACILITY_GROUP[m.kind].order;
}

/** Sélectionne une installation dans le rapport et centre la caméra dessus. */
function pickFacility(meta: { key: string; ref: FacilityMeta['ref'] }): void {
  ui.reportKey.value = meta.key;
  if (meta.ref) focusEntity(meta.ref.kind, meta.ref.id);
  if (meta.ref?.kind === 'building') ui.inspectorService.value = meta.key;
}

function DayKpis(props: { day: UsageDay }) {
  const d = props.day;
  const acc = d.series.accueil;
  const sat: number[] = (acc?.sat ?? []).filter((v) => v >= 0);
  const lostOf = (kind: FacilityMeta['kind']) => d.facilities.filter((f) => f.kind === kind).reduce((n, f) => n + seriesSum(d.series[f.key], 'x', d.n), 0);
  const liftWait = Math.max(0, ...d.facilities.filter((f) => f.kind === 'lift').map((f) => seriesMax(d.series[f.key], 'w', d.n)));
  const jams = dayJams(d);
  const jamMin = jams.reduce((n, j) => n + (j.end - j.start + 1) * 15, 0);
  return (
    <div class="g-kpis">
      <StatTile small label="Forfaits vendus" value={n0(seriesSum(acc, 'n', d.n))} icon={PgTicket} />
      <StatTile small label="Pic de présence" value={n0(seriesMax(acc, 'crowd', d.n))} icon={PgPeople} />
      <StatTile small label="Visiteurs refusés" value={n0(seriesSum(acc, 'x', d.n))} />
      <StatTile small label="Locations manquées" value={n0(lostOf('rental'))} title="File trop longue, parc de skis épuisé" />
      <StatTile small label="Repas manqués" value={n0(lostOf('food'))} title="Salle pleine ou denrées épuisées" />
      <StatTile small label="Pire attente aux remontées" value={liftWait > 0 ? waitText(liftWait) : 'aucune'} />
      <StatTile small label="Satisfaction" value={sat.length ? n0(sat.reduce((a, b) => a + b, 0) / sat.length) : '-'} unit="%" />
      <StatTile small label="Bouchons" value={n0(jams.length)} unit={jams.length > 1 ? 'épisodes' : 'épisode'} title={jamMin ? `Temps saturé cumulé, toutes installations : ${n1(jamMin / 60)} h` : undefined} />
    </div>
  );
}

function Heatmap(props: { day: UsageDay; today: boolean }) {
  const d = props.day;
  const store = hud.store!;
  const [hover, setHover] = useState<{ key: string; i: number } | null>(null);
  const now = props.today ? currentBucket(store.clock.gameNow()) : null;
  const metas = d.facilities.filter((f) => f.kind !== 'stock').sort((a, b) => groupOrder(a) - groupOrder(b));
  const rows: JSX.Element[] = [];
  let group = '';
  for (const meta of metas) {
    const g = FACILITY_GROUP[meta.kind].label;
    if (g !== group) {
      group = g;
      rows.push(
        <div class="g-hm-group" key={`g-${g}`}>
          {g}
        </div>,
      );
    }
    const s = d.series[meta.key];
    const sum = summarizeFacility(meta, s, d.n);
    const Icon = FACILITY_ICON[meta.kind];
    rows.push(
      <div class={`g-hm-row${ui.reportKey.value === meta.key ? ' on' : ''}`} key={meta.key}>
        <button type="button" class="g-hm-label" onClick={() => pickFacility(meta)} title={`${meta.label} : voir le détail`}>
          <Icon size={13} />
          <span>{meta.label}</span>
        </button>
        <UsageStrip kind={meta.kind} series={s} n={d.n} now={now} hover={hover?.key === meta.key ? hover.i : null} onHover={(i) => setHover(i === null ? null : { key: meta.key, i })} onPick={() => pickFacility(meta)} />
        <span class="g-hm-peak num" style={{ color: levelColor(sum.level) }} title="Utilisation maximale">
          {n0(sum.peakU)} %
        </span>
        <span class={`g-hm-lost num${sum.lost > 0 ? ' g-down' : ''}`} title="Clients perdus ou refusés">
          {meta.kind === 'lift' || meta.kind === 'piste' ? '' : sum.lost > 0 ? `-${n0(sum.lost)}` : '0'}
        </span>
      </div>,
    );
  }
  const hoverMeta = hover ? d.facilities.find((f) => f.key === hover.key) : undefined;
  return (
    <div class="g-heatmap">
      <div class="g-hm-row g-hm-axis">
        <span class="g-hm-label" />
        <HourAxis now={now} />
        <span class="g-hm-peak">pic</span>
        <span class="g-hm-lost">perdus</span>
      </div>
      <div class="g-hm-rows">{rows}</div>
      <p class="g-info-line">{hover && hoverMeta ? `${hoverMeta.label} · ${describeBucket(hoverMeta, d.series[hoverMeta.key], hover.i)}` : 'Survolez une case pour le détail du quart d\u2019heure ; cliquez une ligne pour voir l\u2019installation.'}</p>
    </div>
  );
}

function FacilityDetail(props: { day: UsageDay; fkey: string | null; today: boolean }) {
  const d = props.day;
  const [hover, setHover] = useState<number | null>(null);
  const meta = d.facilities.find((f) => f.key === props.fkey) ?? null;
  if (!meta) return <p class="g-empty">Choisissez une installation (ligne de la carte ou bouchon) pour voir son détail.</p>;
  const s = d.series[meta.key];
  const sum = summarizeFacility(meta, s, d.n);
  const fleet = meta.fleet;
  const Icon = FACILITY_ICON[meta.kind];
  const capText =
    meta.kind === 'lift' ? `${n0(meta.cap)} pers./h` : meta.kind === 'ticket' || meta.kind === 'rental' ? `${n0(meta.cap)} clients/h` : meta.kind === 'food' ? `${n0(meta.cap)} places` : meta.kind === 'piste' ? `${n0(meta.cap)} skieurs` : `${n0(meta.cap)} places`;
  return (
    <div class="g-detail">
      <div class="g-detail-head">
        <Icon size={15} />
        <b>{meta.label}</b>
        <span class="g-sub">capacité {capText}{fleet ? ` · parc ${n0(fleet)} paires` : ''}</span>
        <LevelChip level={sum.level} />
      </div>
      <UsageBars kind={meta.kind} series={s} n={d.n} hover={hover} onHover={setHover} now={props.today ? currentBucket(hud.store!.clock.gameNow()) : null} />
      <p class="g-info-line">{hover !== null ? describeBucket(meta, s, hover) : 'Survolez une barre pour le détail.'}</p>
      <div class="g-facts g-facts-3">
        <Fact label="Moyenne" value={`${n0(sum.avgU)} %`} />
        <Fact label="Pic" value={`${n0(sum.peakU)} %`} />
        <Fact label="Saturé" value={sum.jamMin ? `${n0(sum.jamMin)} min` : 'jamais'} tone={sum.jamMin >= 60 ? 'warn' : undefined} />
        <Fact label="Servis" value={n0(sum.served)} />
        {meta.kind !== 'lift' && meta.kind !== 'piste' && <Fact label="Perdus" value={n0(sum.lost)} tone={sum.lost ? 'danger' : undefined} />}
        {(meta.kind === 'lift' || meta.kind === 'ticket' || meta.kind === 'rental') && <Fact label="Attente max" value={sum.maxW ? waitText(sum.maxW) : 'aucune'} />}
        {fleet ? <Fact label="Parc loué" value={`${n0(sum.served)} / ${n0(fleet)}`} tone={sum.served >= fleet ? 'danger' : undefined} /> : null}
      </div>
    </div>
  );
}

function JamList(props: { day: UsageDay }) {
  const jams = dayJams(props.day).slice(0, 8);
  if (!jams.length) return <p class="g-empty">Aucun bouchon ce jour-là : toutes les installations sont restées sous leur capacité.</p>;
  return (
    <ul class="g-jams g-jams-big">
      {jams.map((j) => (
        <li key={`${j.meta.key}-${j.start}`} class={ui.reportKey.value === j.meta.key ? 'on' : ''} onClick={() => pickFacility(j.meta)}>
          <LevelChip level={j.level} />
          <span class="g-jam-text">
            <b>{j.meta.label}</b>
            <span>
              {bucketRange(j.start, j.end)} · {describeJam(j, j.meta.kind)}
            </span>
          </span>
        </li>
      ))}
    </ul>
  );
}

function Season() {
  const store = hud.store!;
  const usage = store.replica!.usage!;
  const today = todayIndex(store.clock.gameNow());
  const latest = usage.days[usage.days.length - 1];
  const columns: { day: number; facilities: FacilitySummary[]; partial?: boolean }[] = usage.summaries.slice(-21).map((s) => ({ day: s.day, facilities: s.facilities }));
  if (latest && latest.n > 0 && !columns.some((c) => c.day === latest.day)) columns.push({ ...summarizeDay(latest), partial: true });
  if (!columns.length) return <p class="g-empty">La première journée complète apparaîtra ici demain matin.</p>;
  const keys = new Map<string, FacilitySummary>();
  for (const c of columns) for (const f of c.facilities) keys.set(f.key, f);
  const rows = [...keys.values()].sort((a, b) => groupOrder(a) - groupOrder(b));
  return (
    <div class="g-season">
      <div class="g-season-head">
        <span class="g-hm-label" />
        {columns.map((c) => (
          <span key={c.day} class="g-season-day" title={dayLabel(c.day, today)}>
            {calendarDay(c.day).dayOfMonth}
          </span>
        ))}
      </div>
      {rows.map((meta) => {
        const Icon = FACILITY_ICON[meta.kind];
        return (
          <div class="g-season-line" key={meta.key}>
            <button type="button" class="g-hm-label" onClick={() => meta.ref && focusEntity(meta.ref.kind, meta.ref.id)}>
              <Icon size={13} />
              <span>{meta.label}</span>
            </button>
            {columns.map((c) => {
              const f = c.facilities.find((x) => x.key === meta.key);
              return (
                <span
                  key={c.day}
                  class={`g-season-cell${c.partial ? ' partial' : ''}`}
                  style={{ background: levelColor(f ? f.level : null) }}
                  title={f ? `${dayLabel(c.day, today)}${c.partial ? ' (en cours)' : ''} : ${levelLabel(f.level)}, pic ${n0(f.peakU)} %, ${f.jamMin} min saturé${f.lost ? `, ${n0(f.lost)} perdus` : ''}${f.worst ? `, pire moment ${bucketRange(f.worst[0], f.worst[1])}` : ''}` : `${dayLabel(c.day, today)} : pas de mesure`}
                >
                  {f ? jamShort(f.jamMin) : ''}
                </span>
              );
            })}
          </div>
        );
      })}
      <p class="g-info-line">Une case par journée : couleur du pire moment, et temps passé saturé ou en bouchon. Survolez une case pour le détail.</p>
    </div>
  );
}

function Frequentation() {
  const store = hud.store!;
  const r = store.replica!;
  const usage = r.usage;
  const game = store.clock.gameNow();
  const today = dayIndex(game);
  const dayNo = ui.reportDay.value;
  const days = usage ? [...usage.days].reverse() : [];
  const day = dayNo === 0 ? undefined : usageDay(usage, dayNo);
  useEffect(() => {
    // Première ouverture : on détaille l'installation du pire bouchon du jour.
    if (!ui.reportKey.value && day) {
      const worst = dayJams(day)[0];
      if (worst) ui.reportKey.value = worst.meta.key;
    }
  }, [day?.day]);
  if (!usage) return <p class="g-empty">Rapport réservé au gérant de la station.</p>;
  return (
    <div class="g-freq">
      <div class="g-freq-bar">
        <div class="g-daychips">
          {days.map((d) => (
            <button key={d.day} class={(dayNo === null ? d === days[0] : d.day === dayNo) ? 'on' : ''} onClick={() => (ui.reportDay.value = d === days[0] ? null : d.day)}>
              <CalendarDays size={12} /> {dayLabel(d.day, today)}
            </button>
          ))}
          <button class={dayNo === 0 ? 'on' : ''} onClick={() => (ui.reportDay.value = 0)}>
            <History size={12} /> Saison
          </button>
        </div>
        <LevelLegend />
      </div>
      {dayNo === 0 ? (
        <Season />
      ) : !day ? (
        <p class="g-empty">Pas de données pour ce jour.</p>
      ) : (
        <>
          <DayKpis day={day} />
          <div class="g-freq-main">
            <Heatmap day={day} today={day.day === today} />
            <div class="g-freq-side">
              <div class="g-section-title">Bouchons détectés</div>
              <JamList day={day} />
              <FacilityDetail day={day} fkey={ui.reportKey.value} today={day.day === today} />
            </div>
          </div>
        </>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Finances                                                             */
/* ------------------------------------------------------------------ */

function Finances() {
  const me = hud.store?.replica?.me;
  if (!me) return <p class="g-empty">Réservé au gérant de la station.</p>;
  const t = me.today;
  const revenue = dayRevenue(t);
  const costs = dayCosts(t);
  const rows: [string, number][] = [
    ['Forfaits', t.passes],
    ['Restauration', t.food],
    ['Location de skis', t.rental],
    ...((t.lodging ?? 0) ? ([['Hébergement', t.lodging ?? 0]] as [string, number][]) : []),
    ...((t.lessons ?? 0) ? ([['Cours de ski', t.lessons ?? 0]] as [string, number][]) : []),
    ['Personnel et maintenance', -t.staff],
    ['Énergie (remontées, neige)', -t.energy],
    ['Entretien des bâtiments', -t.upkeep],
    ['Fournitures', -t.supplies],
    ['Chantiers', -t.construction],
    ['Réparations', -t.repairs],
  ];
  const hist = me.history.slice(-14);
  const max = Math.max(1, ...hist.map((d) => Math.max(dayRevenue(d), dayCosts(d))));
  return (
    <div class="g-office-cols">
      <div>
        <div class="g-kpis g-kpis-3">
          <StatTile small label="Trésorerie" value={euros(me.cash, true)} />
          <StatTile small label="Recettes du jour" value={euros(revenue, true)} />
          <StatTile small label="Résultat du jour" value={euros(revenue - costs, true)} />
        </div>
        <table class="kh-table">
          <tbody>
            {rows.map(([label, v]) => (
              <tr key={label}>
                <td>{label}</td>
                <td style={{ color: v < 0 ? 'var(--alert-2)' : v > 0 ? 'var(--pine-2)' : undefined }}>{euros(v === 0 ? 0 : v)}</td>
              </tr>
            ))}
            <tr>
              <td>
                <b>Visiteurs</b> (refusés)
              </td>
              <td>
                {n0(t.visitors)} ({n0(t.refused)})
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <div>
        <div class="g-section-title">Recettes et charges des derniers jours</div>
        {hist.length === 0 ? (
          <p class="g-empty">Le premier bilan arrive à minuit.</p>
        ) : (
          <div class="g-fin-chart">
            {hist.map((d) => {
              const rev = dayRevenue(d);
              const cost = dayCosts(d);
              const res = rev - cost;
              return (
                <div class="g-fin-day" key={d.day} title={`${calendarDay(d.day).label} : recettes ${euros(rev)}, charges ${euros(cost)}, résultat ${euros(res)}, ${n0(d.visitors)} visiteurs (${n0(d.refused)} refusés), satisfaction ${n0(d.satisfaction)} %`}>
                  <span class="g-fin-bars">
                    <span class="g-fin-rev" style={{ height: `${(rev / max) * 100}%` }} />
                    <span class="g-fin-cost" style={{ height: `${(cost / max) * 100}%` }} />
                  </span>
                  <span class={`g-fin-res ${res >= 0 ? 'g-up' : 'g-down'}`}>{res >= 0 ? <TrendingUp size={10} /> : <TrendingDown size={10} />}</span>
                  <span class="g-fin-label">{calendarDay(d.day).dayOfMonth}</span>
                </div>
              );
            })}
          </div>
        )}
        <div class="g-bars-legend">
          <span>
            <i class="g-key-rev" /> recettes
          </span>
          <span>
            <i class="g-key-cost" /> charges
          </span>
        </div>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Stocks                                                               */
/* ------------------------------------------------------------------ */

function stockTone(ratio: number): Tone {
  return ratio <= 0.05 ? 'danger' : ratio < 0.25 ? 'warn' : 'ok';
}

function Stocks() {
  const r = hud.store?.replica;
  const me = r?.me;
  if (!r || !me) return <p class="g-empty">Réservé au gérant de la station.</p>;
  const game = hud.store!.clock.gameNow();
  const today = usageDay(r.usage, null);
  const stockSeries = today?.series.stocks;
  const card = (key: 'water' | 'fuel' | 'food', label: string, unit: string, Icon: AnyIcon, supply?: SupplyKind) => {
    const s = me.stocks[key];
    const ratio = s.capacity ? s.qty / s.capacity : 0;
    const offer = supply ? r.world.market.find((o) => o.supply === supply) : undefined;
    const daily = s.dailyUse || (key === 'food' ? r.station.stats.onDomain * 0.6 : key === 'fuel' ? 60 : 0);
    const days = daily > 0 ? s.qty / daily : null;
    const qty = supply === 'food' ? 500 : 2000;
    const levels = Array.from({ length: today?.n ?? 0 }, (_, i) => seriesValue(stockSeries, key, i));
    return (
      <div class="sc-rc" key={key}>
        <div class="sc-rc-head">
          <span class="sc-rc-ic">
            <Icon size={16} />
          </span>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div class="sc-rc-name">{label}</div>
            {offer && (
              <div class="sc-rc-price">
                {offer.supplier} (IA) · {n2(offer.price)} €/{unit} · {offer.deliveryHours} h
              </div>
            )}
          </div>
          {levels.some((v) => v >= 0) && <Sparkline values={levels} max={100} color={ratio < 0.25 ? 'var(--ember-2)' : 'var(--glacier-2)'} />}
        </div>
        <div class="sc-rc-qty">
          {n0(s.qty)} <small>{unit} / {n0(s.capacity)}</small>
        </div>
        <Meter ratio={ratio} tone={stockTone(ratio)} />
        <div class="sc-rc-foot">
          <span>{days !== null ? `${days.toLocaleString('fr-FR', { maximumFractionDigits: 1 })} j de stock au rythme actuel` : 'Remplissage naturel de la retenue'}</span>
          {supply && offer && (
            <Button size="sm" variant="secondary" onClick={() => void command({ type: 'orderSupply', supply, qty }, `Commande envoyée à ${offer.supplier}`)}>
              Commander {n0(qty)}
            </Button>
          )}
        </div>
      </div>
    );
  };
  return (
    <div class="g-office-cols">
      <div class="g-stack">
        {card('food', SUPPLY_LABEL.food.label, 'repas', PgCrate, 'food')}
        {card('fuel', SUPPLY_LABEL.fuel.label, 'L', PgFuel, 'fuel')}
      </div>
      <div class="g-stack">
        {card('water', "Retenue d'eau", 'm³', PgDrop)}
        {me.orders.length > 0 && (
          <div>
            <div class="g-section-title">Livraisons en cours</div>
            {me.orders.map((o) => (
              <div class="kh-row" key={o.id}>
                <span class="kh-sub">
                  {n0(o.qty)} {SUPPLY_LABEL[o.supply].unit} · {o.supplier}
                </span>
                <span class="kh-sub num">{o.eta - game > 86400 ? 'demain' : `vers ${formatHour(minuteOfDay(o.eta))}`}</span>
              </div>
            ))}
          </div>
        )}
        <p class="g-sub g-wrap">Les courbes montrent le niveau de chaque stock depuis 8 h (un point par quart d'heure).</p>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Tarifs                                                               */
/* ------------------------------------------------------------------ */

function Tarifs() {
  const r = hud.store?.replica;
  const me = r?.me;
  const [price, setPrice] = useState(me?.ticketPrice ?? ECONOMY.defaultTicketPrice);
  useEffect(() => {
    if (me) setPrice(me.ticketPrice);
  }, [me?.ticketPrice]);
  if (!r || !me) return <p class="g-empty">Réservé au gérant de la station.</p>;
  const fair = fairTicketPrice(r.station.stats.openKm, r.station.stats.openLifts);
  const m = MAINTENANCE_LEVELS[me.maintenance];
  return (
    <div class="g-office-cols">
      <div>
        <div class="g-kpis g-kpis-2">
          <StatTile label="Forfait journée" value={`${n0(price)} €`} small />
          <StatTile label="Prix jugé juste" value={`${n0(fair)} €`} small title="Selon la taille du domaine ouvert : au-dessus, la fréquentation et la satisfaction baissent" />
        </div>
        <input
          class="kh-range"
          type="range"
          min={ECONOMY.minTicketPrice}
          max={ECONOMY.maxTicketPrice}
          value={price}
          onInput={(e) => setPrice(Number((e.target as HTMLInputElement).value))}
          onChange={(e) => void command({ type: 'setTicketPrice', price: Number((e.target as HTMLInputElement).value) }, 'Tarif mis à jour')}
          aria-label="Prix du forfait journée"
        />
        <p class="g-sub g-wrap">
          {price > fair + 3 ? 'Au-dessus du prix juste : moins de visiteurs, et moins contents.' : price < fair - 3 ? 'Sous le prix juste : plus de visiteurs, mais moins de recettes par forfait.' : 'Proche du prix juste.'}
        </p>
      </div>
      <div>
        <div class="g-section-title">Maintenance des remontées</div>
        <div class="kh-seg" style={{ marginTop: '6px' }}>
          {MAINTENANCE_LEVELS.map((lv, i) => (
            <button key={lv.label} class={me.maintenance === i ? 'on' : ''} onClick={() => void command({ type: 'setMaintenance', level: i as 0 | 1 | 2 })}>
              {lv.label}
            </button>
          ))}
        </div>
        <p class="g-sub g-wrap">
          {m.costPerLiftDay > 0 ? `${euros(m.costPerLiftDay)} par remontée et par jour. ` : 'Aucun coût, mais pannes plus fréquentes. '}
          Risque de panne × {m.breakdownFactor.toLocaleString('fr-FR')}.
        </p>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Journal                                                              */
/* ------------------------------------------------------------------ */

function Journal() {
  const store = hud.store!;
  const usage = store.replica?.usage;
  if (!usage) return <p class="g-empty">Journal réservé au gérant de la station.</p>;
  const today = todayIndex(store.clock.gameNow());
  const entries = [...usage.journal].reverse();
  if (!entries.length) return <p class="g-empty">Rien à signaler pour l'instant.</p>;
  const groups: { day: number; list: typeof entries }[] = [];
  for (const e of entries) {
    const d = dayIndex(e.t);
    const g = groups[groups.length - 1];
    if (g && g.day === d) g.list.push(e);
    else groups.push({ day: d, list: [e] });
  }
  return (
    <div class="g-journal">
      {groups.map((g) => (
        <div key={g.day}>
          <div class="g-section-title">
            <ScrollText size={12} /> {dayLabel(g.day, today)} · jour {g.day}
          </div>
          {g.list.map((e) => (
            <EventNotice key={e.id} level={e.level} title={e.title} message={e.text} meta={formatHour(minuteOfDay(e.t))} onTitle={e.ref ? () => focusEntity(e.ref!.kind, e.ref!.id) : undefined} />
          ))}
        </div>
      ))}
    </div>
  );
}
